import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useScheduleFormat } from '../hooks/use-schedule-format';
import { UPCOMING_DAYS, useScheduleTimeline } from '../hooks/use-schedule-timeline';

import { UpcomingList } from './upcoming-list';

/** This schedule's predicted runs for the next four weeks, on its own clock. */
export function UpcomingTab({ schedule }: { schedule: ProductSchedule }) {
  const { t } = useTranslation('schedules');
  const format = useScheduleFormat(schedule.timeZone);
  // Filtered by the backend, so the caps (and the truncation note) are about this schedule only.
  const timeline = useScheduleTimeline({ scheduleId: schedule.id });
  const storeNames = useMemo(
    () => new Map(schedule.stores.map((s) => [s.storeId, s.storeName])),
    [schedule.stores],
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        {t('upcoming.introSchedule', { days: UPCOMING_DAYS, zone: schedule.timeZone })}
      </p>
      {schedule.status !== 'active' ? (
        <p className="text-muted-foreground rounded-md border p-6 text-center text-sm">
          {t(`upcoming.inactive.${schedule.status}`)}
        </p>
      ) : timeline.isError ? (
        <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />
      ) : timeline.isPending ? (
        <div className="flex flex-col gap-2" aria-busy>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-10" />
        </div>
      ) : (
        <UpcomingList
          timeline={timeline.data}
          storeNames={storeNames}
          format={format}
          forSchedule
        />
      )}
      {schedule.status === 'active' ? (
        <p className="text-muted-foreground text-xs">{t('upcoming.endingNote')}</p>
      ) : null}
    </div>
  );
}
